package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 表达式 AST 家族。
 */
sealed interface ExprAst : AstNode {

    /**
     * 字面量表达式：字符串 / 数字 / 布尔。
     */
    sealed interface Literal : ExprAst {
        val value: Any?

        /** 字符串字面量。 */
        data class StringLit(
            override val value: String,
            override val position: SourcePosition,
        ) : Literal

        /** 数值字面量（Long 或 Double）。 */
        data class NumberLit(
            override val value: Number,
            override val position: SourcePosition,
        ) : Literal

        /** 布尔字面量。 */
        data class BoolLit(
            override val value: Boolean,
            override val position: SourcePosition,
        ) : Literal
    }

    /** `state.<name>` 形式的状态引用。 */
    data class StateRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * `session.<name>` 形式的 session 状态引用。
     *
     * <p>session 字段跨 screen / hud 共享同一组 Signal（按字段名全局唯一），
     * 玩家不退出客户端进程期间保持。声明位置在 screen / hud 块内的 `session defaults { ... }`。</p>
     */
    data class SessionRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * `persist.<name>` 形式的持久化状态引用。
     *
     * <p>persist 字段写入磁盘 JSON（按 serverKey + playerUuid 分文件），
     * 玩家退出时自动 flush；DSL 可调 `persist.save() / persist.reload()` 主动落盘 / 重读。
     * 声明位置在 screen / hud 块内的 `persist defaults { ... }`。</p>
     */
    data class PersistRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * `derived.<name>` 形式的派生值引用。
     *
     * <p>derived 字段在运行时是只读的响应式 Computed（懒求值 + 自动多依赖追踪），
     * 声明位置在 screen / hud 块内的 `derived defaults { ... }`。与 [StateRef] 不同，
     * derived 不可写（`derived.x = ...` 在语句层被拒绝）。</p>
     */
    data class DerivedRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * 裸标识符引用（例如 request 回调参数 `payload`）。
     *
     * 普通上下文下该节点不会由 ExpressionParser 产出；仅在 request 回调体
     * 这类具名参数作用域内，作为 MemberAccess 的 target 出现。
     */
    data class IdentRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    /** 字符串插值表达式，由若干文本/表达式片段拼接而成。 */
    data class Interp(
        val parts: List<InterpPart>,
        override val position: SourcePosition,
    ) : ExprAst {
        /** 插值片段：纯文本或嵌入的子表达式。 */
        sealed interface InterpPart {
            /** 文本片段（已解码转义）。 */
            data class TextPart(val text: String) : InterpPart

            /** 表达式片段。 */
            data class ExprPart(val expr: ExprAst) : InterpPart
        }
    }

    /** 二元运算（MVP 仅 `+`）。 */
    data class BinaryOp(
        val op: String,
        val left: ExprAst,
        val right: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = left.range.merge(right.range)
    }


    /**
     * `target.member` 形式的成员访问。
     *
     * 用于 request 回调体中通过参数名访问 payload 字段，例如 `payload.tick`。
     * `state.<name>` 仍由专用 [StateRef] 表示，保持向后兼容。
     */
    data class MemberAccess(
        val target: ExprAst,
        val member: String,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }

    /**
     * `target.method(args...)` 形式的成员方法调用。
     *
     * 用于 action body 中出现的内建方法调用（如 `local.openScreen("id")`、`local.back()`）。
     * 语义由运行时根据 [target] 的实际类型分派，MVP 仅处理 `LocalApi` 目标。
     */
    data class MemberCall(
        val target: ExprAst,
        val method: String,
        val args: List<ExprAst>,
        override val position: SourcePosition,
        /** Stage 4：命名实参支持，仅 net.request 等内置方法迁移到描述符路径时使用。 */
        val namedArgs: List<NamedArg> = emptyList(),
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }

    /** `theme.<key>` 形式的主题 token 引用。 */
    data class ThemeRef(
        val key: String,
        override val position: SourcePosition,
    ) : ExprAst

    /** `props.<name>` 形式的组件 prop 引用，仅出现在 component root 内，展开阶段被替换。 */
    data class PropsRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    // ===== C1 新增节点 =====

    /** `null` 字面量。 */
    data class NullLit(
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * 一元运算。
     *
     * - `op` 取值：`-`（数值取反）/ `!`（逻辑非）
     */
    data class UnaryOp(
        val op: String,
        val operand: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst

    /** `target?.member` 安全成员访问；target 为 null 时短路返回 null。 */
    data class SafeMemberAccess(
        val target: ExprAst,
        val member: String,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }

    /**
     * `target!!` 非空断言（M10 Stage 6 引入）。
     *
     * 编译期类型断言：target 类型若为 Nullable(T) 则窄化为 T，让后续解引用不再触发 E666。
     * Runtime 不强制空检查——wire 序列化时直接降级为 target 自身（plan §9.2：wire format
     * 不感知本节点）。
     *
     * 链式形态 `a!!.b!!.c` 由 parser 构造为右结合：`(((a)!!).b)!!).c`。
     */
    data class NonNullAssert(
        val target: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }

    /** `target?.method(args...)` 安全成员方法调用；target 为 null 时短路返回 null。 */
    data class SafeMemberCall(
        val target: ExprAst,
        val method: String,
        val args: List<ExprAst>,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }

    /** `left ?: right` Elvis：left 非 null 取 left，否则取 right。 */
    data class Elvis(
        val left: ExprAst,
        val right: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst

    /** `cond ? thenExpr : elseExpr` 三元表达式。 */
    data class Ternary(
        val cond: ExprAst,
        val thenExpr: ExprAst,
        val elseExpr: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst

    /** `if (cond) thenExpr else elseExpr` 表达式。 */
    data class IfExpr(
        val cond: ExprAst,
        val thenBody: List<StmtAst>,
        val elseBody: List<StmtAst>,
        override val position: SourcePosition,
    ) : ExprAst

    /** 局部变量引用。
     *
     * 由 ExpressionParser 在解析 IDENTIFIER 时根据当前作用域决定是否升格为
     * LocalRef；与 [IdentRef] 区别在于：LocalRef 一定指向当前函数/动作体内
     * 声明的 `var`，语义上必须能解析到声明。
     */
    data class LocalRef(
        val name: String,
        override val position: SourcePosition,
    ) : ExprAst

    // ===== C2 新增节点 =====

    /** `[a, b, c]` 列表字面量；空列表 `[]` 合法。 */
    data class ListLiteralExpr(
        val elements: List<ExprAst>,
        override val position: SourcePosition,
    ) : ExprAst

    // ===== M6-a 新增节点：Map 字面量 =====

    /**
     * `#{"k1": v1, "k2": v2}` Map 字面量；空 `#{}` 合法。
     *
     * - 键必须是字符串字面量（parser 阶段强制；非字符串键报 E736）
     * - 顺序保留（运行期求值为 LinkedHashMap）
     * - 作为 data 实例化语法时由 [top.wcpe.allincore.dsl.compiler.semantic.DataLiteralChecker]
     *   按目标 data 类型做字段名/必填/类型校验
     */
    data class MapLiteralExpr(
        val entries: List<MapEntry>,
        override val position: SourcePosition,
    ) : ExprAst

    /** Map 字面量条目：键为字符串字面量原文，值为任意表达式。 */
    data class MapEntry(
        val key: String,
        val value: ExprAst,
        val position: SourcePosition,
    )

    /** 范围类型。 */
    enum class RangeKind { CLOSED, HALF_OPEN }

    /** `start..end`（闭区间） / `start until end`（半开）。两侧均要求数值。 */
    data class RangeExpr(
        val start: ExprAst,
        val endValue: ExprAst,
        val kind: RangeKind,
        override val position: SourcePosition,
    ) : ExprAst

    /** `target[index]` 索引访问。 */
    data class IndexExpr(
        val target: ExprAst,
        val index: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * `when (subject) { values -> body ... else -> body }` 多分支表达式。
     *
     * 同一节点同时承载语句与表达式语义：作为 [StmtAst.WhenStmt] 的 payload 时丢弃返回值，
     * 作为表达式时取匹配分支 body 末尾 ExprStmt 的求值结果（无匹配且无 else 时为 null）。
     */
    data class WhenExpr(
        val subject: ExprAst,
        val branches: List<WhenBranch>,
        /** else 分支体；不存在 else 时为 null。 */
        val elseBody: List<StmtAst>?,
        override val position: SourcePosition,
    ) : ExprAst {
        /** when 单个分支：`v1, v2 -> { body }`。 */
        data class WhenBranch(
            val values: List<ExprAst>,
            val body: List<StmtAst>,
            val position: SourcePosition,
        )
    }

    // ===== C3 新增节点：lambda 一等公民 =====

    /**
     * lambda 表达式：`{ x, y -> body }` 或 `{ body }`（隐式 `it`）。
     *
     * 隐式 `it` 形态在 parser 阶段统一规范化为 `params = listOf("it")`，
     * AST/序列化层不再区分显隐式。
     *
     * body 末尾若为 [StmtAst.ExprStmt]，其表达式值作为 lambda 的返回值。
     */
    data class LambdaExpr(
        val params: List<String>,
        val body: List<StmtAst>,
        override val position: SourcePosition,
        /**
         * M4 A2 Stage 1：是否为 suspend lambda（body 顶层位置含 await）。
         *
         * 由 [LambdaSuspendDetector] 在 parser 构造时填充；其它构造点保持默认 false
         * 以兼容现存测试与序列化路径。Stage 1 仅在 AST + 语义层消费此字段，
         * wire format / runtime 不感知。
         */
        val isSuspend: Boolean = false,
        /** M5-b：各形参的默认值表达式；与 [params] 等长，无默认值为 null。 */
        val defaults: List<ExprAst?> = emptyList(),
        /** M5-c：各形参的类型标注；与 [params] 等长，无标注为 null。空列表表示全部未标注。 */
        val paramTypes: List<TypeAst?> = emptyList(),
    ) : ExprAst

    /**
     * `::name` 函数引用。
     *
     * - 无 boundArgs/boundNamedArgs：纯引用对象（`::name` 或 `::name()`）
     * - 有 boundArgs/boundNamedArgs（M6-e）：部分应用，`::name(args)` 在创建时 snapshot
     *   实参，调用时与运行期实参（通常为空）合并后转发给目标 fun
     */
    data class FunRefExpr(
        val name: String,
        override val position: SourcePosition,
        /** M6-e：`::fun(args)` 中绑定的位置实参，创建时即被 snapshot 求值。 */
        val boundArgs: List<ExprAst> = emptyList(),
        /** M6-e：`::fun(name = expr)` 命名实参；语义同 boundArgs。 */
        val boundNamedArgs: List<NamedArg> = emptyList(),
    ) : ExprAst

    // ===== C5 新增节点：net.request 单回调形态 =====

    /**
     * `net.request("ns.handler", { payload }, callback, timeoutMs = N)` 调用。
     *
     * - [namespace] / [handler]：从 handlerKey 字面量 `"ns.handler"` 拆分（必须含 '.'）
     * - [payload]：复用旧 RequestStmt 的 PayloadEntry（key = expr 列表）
     * - [callback]：仅允许 [LambdaExpr] 或 [FunRefExpr]；trailing lambda 也归并到此
     * - [timeoutMs]：命名参数 `timeoutMs = N`，可省略
     *
     * 语义层面是"调用"，但本阶段仅作语句使用；C6 await 才放开为表达式值。
     */
    data class NetRequestCall(
        val namespace: String,
        val handler: String,
        val payload: List<NetPayloadEntry>,
        /** await 形态下可为 null：codegen 由 [AwaitExpr] 路径单独处理。 */
        val callback: ExprAst?,
        val timeoutMs: ExprAst?,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * net.request payload 单个条目：`key = expr`。
     *
     * 独立 data class，与已废弃的 `StmtAst.RequestStmt.PayloadEntry` 解耦。
     */
    data class NetPayloadEntry(
        val key: String,
        val value: ExprAst,
        val position: SourcePosition,
    )

    /**
     * Stage B：`net.subscribe("ns.topic", handler)` 内置调用 AST 节点。
     *
     * 与 [NetRequestCall] 完全对称的独立 ExprAst 节点：
     * - 第 1 参 `"namespace.topic"` 字符串字面量（与 net.request 相同形式），由 parser 拆为
     *   [namespace] + [topic] 两个 String，避免在 IR 中夹一对零碎的 String 实参；
     * - 第 2 参 [handler] 是 lambda（1 形参）或 funRef，runtime 求值得到可调用对象后注册到
     *   SubscribeRouter；
     * - 求值结果是 SubscriptionValue，DSL 中可 `var s = net.subscribe(...); s.cancel()`。
     */
    data class NetSubscribeCall(
        val namespace: String,
        val topic: String,
        val handler: ExprAst,
        override val position: SourcePosition,
    ) : ExprAst

    /**
     * 通用调用表达式：`callee(args...)`。
     *
     * - callee 可为 [IdentRef]/[LocalRef]/[FunRefExpr]/[LambdaExpr] 等
     * - trailing lambda 在解析阶段合并到 [args] 末尾，不再单独区分
     * - M5-b：新增 [namedArgs] 支持命名实参
     */
    data class CallExpr(
        val callee: ExprAst,
        val args: List<ExprAst>,
        override val position: SourcePosition,
        /** M5-b：命名实参列表；位置实参在前，命名实参在后。 */
        val namedArgs: List<NamedArg> = emptyList(),
    ) : ExprAst {
        override val range
            get() = callee.range.merge(position.range)
    }

    // ===== C6 新增节点：await + builtin =====

    /**
     * `await net.request(...)` 表达式。
     *
     * MVP 仅允许包裹 [NetRequestCall]，并且只能出现在两种位置：
     * - 语句位置：`await net.request(...)`（裸 await，丢弃 result）
     * - var 初始化器：`var x = await net.request(...)`（绑定 result）
     *
     * 出现在其它表达式位置（如普通调用参数）由 codegen 阶段拒绝。
     */
    /**
     * `await net.request(...)` 表达式（可带后缀链）。
     *
     * 出现位置：
     * - 语句位置：`await net.request(...)`（裸 await，丢弃 result）
     * - var 初始化器：`var x = await net.request(...)`（绑定 result）
     * - var 初始化器 + 后缀链：`var x = await net.request(...).payload.string("k")`
     *
     * 当 [tailExpr] 非 null 时，表示 await 后接成员/方法/索引链；其内部的根
     * NetRequestCall 已被替换为 `IdentRef("__result")` 占位，运行期把真实 result
     * 注入到 locals["__result"] 后即可整体求值得到链尾值。
     */
    data class AwaitExpr(
        /**
         * 包裹 `net.request(...)` 的形态；与 [suspendCall] 二选一互斥，至少一个非 null。
         *
         * Stage 5 起类型从 NetRequestCall? 放宽为 ExprAst?，以承载 parser 产出的
         * MemberCall（target=IdentRef("net"), method="request"）过渡态。
         * NativeClassResolverPass 在 SemanticChecker 之前把 MemberCall lower 为
         * NetRequestCall；codegen 阶段保证此字段一定是 NetRequestCall（lower 后）。
         */
        val netRequestCall: ExprAst?,
        override val position: SourcePosition,
        val tailExpr: ExprAst? = null,
        /**
         * M4 A2 Stage 1：`await f(args)` 形态，f 为 suspend lambda 局部变量。
         *
         * 与 [netRequestCall] 互斥；非 null 时 [tailExpr] 必须为 null
         * （suspend lambda 返回值即 lambda body 末位表达式值，不支持 net.request
         * 风格的 payload 链）。
         *
         * 通用 await 阶段（任务 3）扩展：除 [CallExpr] 外，类型放宽到 [ExprAst]，
         * 支持 [MemberCall] 形态（如 {@code await timer.delayAsync(1000)}）。
         * 运行期由 IIFE-suspend-lambda 包装求值，结果若实现 PendingAwaitable 则
         * 自动等待，否则按同步返回值立即恢复。
         */
        val suspendCall: ExprAst? = null,
    ) : ExprAst {
        init {
            // 互斥校验：必须恰好提供一种形态
            require((netRequestCall != null) xor (suspendCall != null)) {
                "AwaitExpr 必须且仅能提供 netRequestCall 或 suspendCall 之一"
            }
            require(suspendCall == null || tailExpr == null) {
                "AwaitExpr.suspendCall 路径不支持 tailExpr"
            }
        }
    }

    /**
     * 内建调用 `print(expr)` / `log(expr)`：转发到运行时 logger。
     *
     * 仅在 callee 标识符未被局部 var / screen 级 fun 遮蔽时由 parser 识别。
     */
    data class BuiltinCallExpr(
        val name: String,
        val args: List<ExprAst>,
        override val position: SourcePosition,
        /**
         * M6-h Round 2：命名实参支持（toast 等内建函数使用）。默认空列表保留向后兼容。
         */
        val namedArgs: List<NamedArg> = emptyList(),
    ) : ExprAst

    // ===== M5-c 新增节点：is 类型判断 =====

    /**
     * `target is TypeName` 类型判断表达式（M5-c）。
     *
     * 第一版仅支持基础内置类型名（Number/String/Boolean/Map/List/Any/Unit），
     * 由运行时按 instanceof / 数值 / 字符串 等约定做精确匹配。
     */
    data class IsExpr(
        val target: ExprAst,
        val typeName: String,
        override val position: SourcePosition,
    ) : ExprAst {
        override val range
            get() = target.range.merge(position.range)
    }
}
